import { useRef, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { Box } from '@mui/material';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import FastRewindRoundedIcon from '@mui/icons-material/FastRewindRounded';
import FastForwardRoundedIcon from '@mui/icons-material/FastForwardRounded';
import { filterUndefined } from '../../utils/FilterUndefinedObject';
import './GridList.scss';

function removeAriaHiddenFromSlickSlides() {
  const slickSlides = document.querySelectorAll('.slick-slide');
  slickSlides.forEach((slide) => {
    slide.setAttribute('aria-hidden', 'false');
  });
}

/**
 * Grid list component to display list of element e.g. lesson cards
 *
 * @param {*} props
 *
 * titleName: Title of the slidablelist
 * children: elements to display in the list
 */
const defaultProps = {
  gradient: true,
  children: <div />,
  canSwipe: true,
  subTitle: null,
  displayFastNavigator: false,
  placeHolderChildren: null,
  onTitleClick: null,
};

export default function GridList(props) {
  const {
    titleName,
    subTitle,
    slidesToScroll,
    children,
    canSwipe,
    gradient,
    displayFastNavigator,
    placeHolderChildren,
    onTitleClick,
    'data-test': dataTest,
  } = { ...defaultProps, ...filterUndefined(props) };
  const [state, setState] = useState({
    previousButtonDisable: true,
    nextButtonDisable: true,
  });

  const childrenLength = children.length;

  useEffect(
    () => {
      if (children.length > 0) {
        setState({
          ...state,
          nextButtonDisable: (0 + slidesToScroll) >= childrenLength,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children],
  );

  useEffect(
    () => {
      removeAriaHiddenFromSlickSlides();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const sliderRef = useRef(null);

  const afterSliderChage = (newIndex) => {
    setState({
      ...state,
      previousButtonDisable: newIndex <= 0,
      nextButtonDisable: (newIndex + slidesToScroll) >= childrenLength,
    });
    removeAriaHiddenFromSlickSlides();
  };

  const settings = {
    infinite: false,
    speed: 500,
    variableWidth: true,
    slidesToScroll,
    // slidesToShow,
    // lazyLoad: 'ondemand',
    className: 'slider',
    ref: sliderRef,
    afterChange: afterSliderChage,
    swipe: canSwipe,
  };

  const next = () => {
    if (!state.nextButtonDisable) {
      sliderRef.current.slickNext();
    }
  };

  const previous = () => {
    if (!state.previousButtonDisable) {
      sliderRef.current.slickPrev();
    }
  };

  const fastBackward = () => {
    if (!state.previousButtonDisable) {
      sliderRef.current.slickGoTo(0);
    }
  };

  const fastForward = () => {
    if (!state.nextButtonDisable) {
      sliderRef.current.slickGoTo(children.length - 1);
    }
  };

  return (
    <div className={`grid-list-container ${gradient ? 'blue-gradient' : ''}`} data-test={dataTest}>
      <h2 className='grid-list-container-header-for-vizzle-test'>
        {displayFastNavigator && (
          <FastRewindRoundedIcon
            className={`grid-list-fast-navigate ${state.previousButtonDisable ? 'grid-list-navigate-disable' : ''}`}
            onClick={fastBackward}
            tabIndex={state.previousButtonDisable ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                fastBackward();
              }
            }}
            aria-hidden={state.previousButtonDisable ? 'true' : 'false'}
          />
        )}

        <PlayCircleFilledIcon
          className={`margin-right-medium grid-list-navigate-previous ${state.previousButtonDisable ? 'grid-list-navigate-disable' : ''}`}
          onClick={previous}
          tabIndex={state.previousButtonDisable ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              previous();
            }
          }}
          aria-hidden={state.previousButtonDisable ? 'true' : 'false'}
        />
        {subTitle && (
          <span className='sub-title'>
            {subTitle}
          </span>
        )}
        {onTitleClick ? (
          <Box
            className='clickable-title'
            onClick={() => {
              onTitleClick();
            }}
          >
            {titleName}
          </Box>
        ) : (
          <span data-dd-privacy='mask'>
            {titleName}
          </span>
        )}
        <PlayCircleFilledIcon
          className={`margin-left-medium grid-list-navigate-next ${state.nextButtonDisable ? 'grid-list-navigate-disable' : ''}`}
          onClick={next}
          tabIndex={state.nextButtonDisable ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              next();
            }
          }}
          aria-hidden={state.nextButtonDisable ? 'true' : 'false'}
        />
        {displayFastNavigator && (
          <FastForwardRoundedIcon
            className={`grid-list-fast-navigate ${state.nextButtonDisable ? 'grid-list-navigate-disable' : ''}`}
            onClick={fastForward}
            tabIndex={state.nextButtonDisable ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                fastForward();
              }
            }}
            aria-hidden={state.nextButtonDisable ? 'true' : 'false'}
          />
        )}
      </h2>
      {children && (
        <Slider {...settings}>
          {children}
          <div />
        </Slider>
      )}

      {placeHolderChildren}
    </div>
  );
}
